import React from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { setBookingStatus } from '../../../../core/state/bookings/asyncActions';
import { BookingStatus } from '../../../../core/enums/BookingStatus';
import { CancellationReason } from '../../../../core/enums/CancellationReason';
import { Color } from '../../../../core/enums/Color';

import Modal from '../../../components/modal/Modal';
import Button from '../../../../components/form/button/Button';
import TextArea from '../../../../components/form/text-area/TextArea';
import Booking from '../../../../core/types/model/Booking';
import { isMobileScreenSize } from '../../../../core/utils/isMobileScreenSize';
import { useInternalUser } from '../../../../core/hooks/useInternalUser';

interface Props {
  booking: Booking;
  isOpen: boolean;
  closeModal: () => void;
  onSubmit?: () => void;
}

const validationSchema = yup
  .object()
  .shape({
    cancellationReason: yup.string().required()
  })
  .required();

const BookingCancellationModal: React.FC<Props> = ({ booking, isOpen, closeModal, onSubmit }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isMobileScreen = isMobileScreenSize();
  const isExternalUser = !useInternalUser();

  const {
    handleSubmit,
    control,
    formState: { isValid }
  } = useForm({
    defaultValues: {
      cancellationReason: ''
    },
    resolver: yupResolver(validationSchema),
    mode: 'onChange'
  });

  const confirmCancellation = (formData: { cancellationReason: string }) => {
    if (onSubmit) {
      onSubmit();
    }
    dispatch(setBookingStatus(booking, BookingStatus.Canceled, CancellationReason.ManualCancelation, formData.cancellationReason));
  };

  const title = <h3 className="modal-title">{t(`booking.cancel.modal.title${isExternalUser ? '.external' : ''}`)}</h3>;
  const buttons = (
    <div className="modal-buttons">
      <Button onClick={closeModal} buttonTypes={!isMobileScreen ? ['transparent'] : ['outline', 'block']} color={Color.Blue}>
        {t('common.cancel')}
      </Button>
      <Button
        type="submit"
        buttonTypes={!isMobileScreen ? ['primary'] : ['primary', 'block']}
        color={Color.Blue}
        form="booking-cancellation-form"
        disabled={!isValid}
      >
        {t('common.confirm')}
      </Button>
    </div>
  );

  return (
    <Modal
      className={isMobileScreen ? 'modal--small-mobile' : ''}
      height={isMobileScreen ? 423 : undefined}
      isOpen={isOpen}
      closeModal={closeModal}
      title={title}
      toolbar={buttons}
    >
      <form id="booking-cancellation-form" onSubmit={handleSubmit(confirmCancellation)}>
        <Controller
          name="cancellationReason"
          control={control}
          render={({ field: { onChange, name, value } }) => (
            <TextArea
              label={t(`booking.cancel.modal.label${isExternalUser ? '.external' : ''}`)}
              id="cancellation-reason"
              name={name}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              required
            />
          )}
        />
      </form>
    </Modal>
  );
};

export default BookingCancellationModal;
